import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReferenceErrorRow } from '@1po/1po-bff-fe-spec/generated/basket/response/model/ReferenceErrorRow';
import { List } from 'antd';
import { CartReferenceErrorRow } from 'domains/basket/Basket.types';
import { Box, ErrorAlert, Flex, Text } from 'UI';
import { TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE } from 'utils/rights/tractionBatteryOrder';
import { ErrorDetails, ScrollContainer, SDivider } from './FileReferencesRowsErrors.styled';

interface EListI {
  data: CartReferenceErrorRow[];
}

interface SplitListI {
  leftList: CartReferenceErrorRow[];
  rightList: CartReferenceErrorRow[];
}

interface FileReferencesRowsErrorsProps {
  errorRows: CartReferenceErrorRow[];
}

type FileUploadErrorCode =
  | ReferenceErrorRow['errorCode']
  | 'TRACTION_BATTERY_ORDER_NOT_AUTHORIZED'
  | 'TRACTION_BATTERY_VIN_REQUIRED'
  | 'TRACTION_BATTERY_VIN_NON_COMPLIANT';

export default function FileReferencesRowsErrors({ errorRows }: Readonly<FileReferencesRowsErrorsProps>) {
  const { t } = useTranslation();
  const [isHidden, setIsHidden] = useState<boolean>(true);
  const [splitList, setSplitList] = useState<SplitListI>({ leftList: [], rightList: [] });

  function EList(props: EListI) {
    return (
      <List
        dataSource={props.data}
        renderItem={(item) => (
          <List.Item data-cy={`error-row-${item.referenceNumber}`}>
            {item.rowNumber && (
              <>
                <Text type="base" bold>
                  {`${t('common.line', 'Line')} ${item.rowNumber}`}
                </Text>
                <Box width={5} />
              </>
            )}
            <Text type="base" color="grey35">
              {item.referenceNumber}:
            </Text>
            <Box width={5} />
            <Text type="base" color="grey35">
              {translateErrorCode(item.errorCode)}
            </Text>
            <Flex />
          </List.Item>
        )}
        split={false}
      />
    );
  }

  function translateErrorCode(code: FileUploadErrorCode): string {
    switch (code) {
      case 'UNKNOWN_REFERENCE':
        return t('cart.file_upload.row.unknown_reference', 'Unknown reference');
      case 'NOT_SOLD':
        return t('cart.file_upload.row.product_no_longer_sold', 'Product no longer sold');
      case 'TECHNICAL_ERROR':
        return t('cart.file_upload.row.unknown_reference', 'Unknown reference');
      case 'INVALID_QUANTITY':
        return t('cart.file_upload.row.invalid_quantity', 'Invalid quantity');
      case 'QUANTITY_EXCEEDS_999':
        return t('cart.file_upload.row.quantity_exceeds_999', 'Quantity exceeds 999');
      case 'OUT_OF_STOCK':
        return t('cart.file_upload.row.out_of_stock', 'Product is out of stock');
      case 'PRICE_NOT_AVAILABLE':
        return t('cart.file_upload.row.price_unavailable', 'Product price unavailable');
      case 'TRACTION_BATTERY_ORDER_NOT_AUTHORIZED':
        return t(
          'cart.file_upload.row.traction_battery_order_not_authorized',
          'You are not allowed to order traction battery parts',
        );
      case 'TRACTION_BATTERY_VIN_REQUIRED':
        return t(
          'cart.file_upload.row.traction_battery_vin_required',
          'Identify car with VIN to order traction battery part',
        );
      case 'TRACTION_BATTERY_VIN_NON_COMPLIANT':
        return t(
          'cart.file_upload.row.traction_battery_vin_non_compliant',
          TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE,
        );
      default:
        return '';
    }
  }

  useEffect(() => {
    const half = Math.ceil(errorRows.length / 2);
    setSplitList({
      leftList: errorRows.slice(0, half),
      rightList: errorRows.slice(half),
    });
  }, [errorRows]);

  if (errorRows.length === 0) {
    return null;
  }

  return (
    <Flex direction={'column'} align={'flex-start'} justify={'center'}>
      <ErrorAlert
        data-cy={'items-not-added-alert'}
        message={
          <Flex direction={'row'} justify={'space-between'}>
            <Text type="base" color="grey35">
              {t('cart.file_upload.items_could_not_be_added', "One or several items couldn't be added to your cart")}
            </Text>
            <Flex minWidth={100} justify={'flex-end'}>
              <Text
                type={'text'}
                displayStyle={'link'}
                cursor={'pointer'}
                onClick={() => setIsHidden(!isHidden)}
                data-cy={isHidden ? 'show-details-link' : 'close-details-link'}
              >
                {isHidden
                  ? t('cart.file_upload.see_details', 'See details')
                  : t('cart.file_upload.close_details', 'Close details')}
              </Text>
            </Flex>
          </Flex>
        }
      />
      <ScrollContainer>
        <ErrorDetails hidden={isHidden}>
          <Box align={'left'} data-cy={'error-details'}>
            <Text type={'text'} displayStyle={'error'}>
              {t('common.details', 'Details')}
            </Text>
            <Flex>
              <Flex>
                <EList data={splitList.leftList} />
              </Flex>
              {splitList.rightList.length > 0 && (
                <>
                  <SDivider type={'vertical'} />
                  <Flex>
                    <EList data={splitList.rightList} />
                  </Flex>
                </>
              )}
            </Flex>
          </Box>
        </ErrorDetails>
      </ScrollContainer>
    </Flex>
  );
}
